import { useFocusEffect, useRoute } from "@react-navigation/native";
import AssessmentMenu from "../../../components/navigation/AssessmentMenu/AssessmentMenu";
import { useCallback, useRef } from "react";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import { RouteProp } from "@react-navigation/native";
import { useGetAssessmentQuery } from "../../../graphql/generated/graphql";
import { formatCountdownFromSeconds, normalizeTimeFormat } from "../../../utils/timeUtils";

interface AssessmentMenuContainerProps {
  onComplete: () => void;
  handleExitPress: () => void;
  defaultTime: number | null;
  setDisplayTime: (time: string) => void;
  displayTime: string | null;
  activeAssessmentId: string;
  isTerminatingAssessment: boolean;
  hasMicrophoneControls: boolean;
  isSseAssessment?: boolean;
  selectedDeviceId?: string;
  onDeviceSelect?: (deviceId: string) => void;
}

function decrementTime(time: string): string {
  const [minutes, seconds] = time.split(":").map(Number);
  const totalSeconds = minutes * 60 + seconds - 1;
  return formatCountdownFromSeconds(totalSeconds);
}

function AssessmentMenuContainer({
  onComplete,
  handleExitPress,
  defaultTime,
  setDisplayTime,
  displayTime,
  activeAssessmentId,
  isTerminatingAssessment,
  hasMicrophoneControls,
  isSseAssessment = false,
  selectedDeviceId = "",
  onDeviceSelect,
}: AssessmentMenuContainerProps) {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course Assessment">>();
  const hasMounted = useRef<boolean>(false);
  const assessmentId = route.params?.assessment_id;

  const { data: assessment } = useGetAssessmentQuery({
    variables: {
      assessmentId,
    },
    skip: !assessmentId,
  });

  const moduleName = assessment?.getAssessment?.data?.moduleHasAssessment?.title;

  useFocusEffect(
    useCallback(() => {
      if (!activeAssessmentId || !defaultTime) {
        return;
      }

      if (activeAssessmentId !== assessmentId) {
        console.error("Assessment ID mismatch");
        setDisplayTime(normalizeTimeFormat(`${defaultTime}:00`));
        return;
      }

      const intervalId = setInterval(() => {
        const newTime = displayTime === "00:00" ? "00:00" : decrementTime(displayTime);

        if (newTime === "00:00") {
          clearInterval(intervalId);
        }

        setDisplayTime(normalizeTimeFormat(newTime));
      }, 1000);

      return () => clearInterval(intervalId);
    }, [assessmentId, activeAssessmentId, displayTime, defaultTime])
  );

  useFocusEffect(
    useCallback(() => {
      if (!hasMounted.current) {
        hasMounted.current = true;
        return;
      }

      if (displayTime === "00:00" && !isTerminatingAssessment) {
        onComplete();
      }
    }, [displayTime, isTerminatingAssessment])
  );

  return (
    <AssessmentMenu
      handleExitPress={handleExitPress}
      remainingTime={displayTime}
      moduleName={moduleName}
      hasMicrophoneControls={hasMicrophoneControls}
      timeIsLoading={!displayTime || !defaultTime}
      isSseAssessment={isSseAssessment}
      selectedDeviceId={selectedDeviceId}
      onDeviceSelect={onDeviceSelect}
    />
  );
}

export default AssessmentMenuContainer;
